import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { X } from 'lucide-react';

const ICONS = ['📋', '🚀', '💡', '🔥', '🎯', '🛠', '🧩', '📊', '🌟'];
const COLORS = ['#4f63ff', '#f59e0b', '#10b981', '#ef4444', '#a78bfa', '#f97316', '#06b6d4', '#ec4899'];

export default function CreateProjectModal({ onClose }) {
  const [form, setForm] = useState({ name: '', key: '', description: '', icon: '📋', color: '#4f63ff', group: null });
  const [loading, setLoading] = useState(false);
  const { createProject, projectGroups } = useStore();
  const navigate = useNavigate();

  const autoKey = (name) => name.toUpperCase().replace(/[^A-ZА-Я0-9]/gi, '').slice(0, 5) || '';

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, group: form.group ? (typeof form.group === 'string' ? form.group : form.group._id) : null };
      const p = await createProject(payload);
      onClose();
      navigate(`/project/${p._id}`);
    } catch (err) {
      alert(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal p-6 max-w-md">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold">Новый проект</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Icon and Color */}
          <div>
            <label className="label">Иконка</label>
            <div className="flex gap-1.5 flex-wrap">
              {ICONS.map(ic => (
                <button key={ic} type="button" onClick={() => setForm(p => ({ ...p, icon: ic }))}
                  className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-all ${form.icon === ic ? 'ring-2 ring-brand-500 bg-brand-50 dark:bg-brand-900' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                  {ic}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Цвет</label>
            <div className="flex gap-2">
              {COLORS.map(c => (
                <button key={c} type="button" onClick={() => setForm(p => ({ ...p, color: c }))}
                  className={`w-7 h-7 rounded-full transition-transform ${form.color === c ? 'scale-125 ring-2 ring-offset-1 ring-gray-400' : 'hover:scale-110'}`}
                  style={{ background: c }} />
              ))}
            </div>
          </div>

          <div>
            <label className="label">Название *</label>
            <input className="input" placeholder="Мой проект" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value, key: autoKey(e.target.value) }))} required />
          </div>

          <div>
            <label className="label">Ключ проекта *</label>
            <input className="input uppercase" placeholder="PROJ" value={form.key} maxLength={6}
              onChange={e => setForm(p => ({ ...p, key: e.target.value.toUpperCase() }))} required />
            <p className="text-xs text-gray-400 mt-1">Используется для номеров задач: {form.key || 'PROJ'}-1</p>
          </div>

          <div>
            <label className="label">Описание</label>
            <textarea className="input resize-none" rows={2} placeholder="Описание проекта..."
              value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>

          {projectGroups.length > 0 && (
            <div>
              <label className="label">Группа проекта</label>
              <select className="input" value={typeof form.group === 'string' ? form.group : form.group?._id || ''}
                onChange={e => setForm(p => ({ ...p, group: e.target.value ? projectGroups.find(g => g._id === e.target.value) : null }))}>
                <option value="">Без группы</option>
                {projectGroups.map(g => (
                  <option key={g._id} value={g._id}>{g.icon} {g.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Отмена</button>
            <button type="submit" className="btn-primary flex-1 justify-center" disabled={loading}>
              {loading ? 'Создание...' : 'Создать проект'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
